import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { TaskGroup, TaskGroupForm, TaskGroupSearchForm } from 'entities/task_group'

type TaskGroupsResponse = PagingResponse & {
    results: TaskGroup[]
}

export function useFetchTaskGroupsApi(searchForm?: Form<TaskGroupSearchForm>): IndexApiSet<TaskGroupsResponse> & { execute: () => void } {
    const apiPath = 'task_groups/'
    const api = useIndexApi<TaskGroupsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchTaskGroupApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `task_groups/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTaskGroupApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskGroupForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TaskGroupForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<TaskGroupForm>) => {
        const apiPath = `task_groups/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTaskGroupApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskGroupForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskGroupForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<TaskGroupForm>) => {
        const apiPath = `task_groups/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTaskGroupsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskGroupForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskGroupForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskGroupForm[]>) => {
        const apiPath = `task_groups/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTaskGroupApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `task_groups/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
